import {getTestProps} from '@benchling/js-test-utils';
import {Color} from '@benchling/nucleus-ui';
import {createStyles, keyframes} from '@mantine/core';
import {css} from 'aphrodite';
import React from 'react';

import {
  QUEUED_TOOL_DISPLAY_LABEL,
  SKIPPED_TOOL_DISPLAY_LABEL,
  ToolCallStatus,
} from 'llm/chat-library/progress/ProgressTypes';
import {getToolCallVisualOpacity} from 'llm/chat-library/progress/ToolCallVisualState';
import {ToolIconDisplay} from 'llm/chat-library/tool-details/ToolIconDisplay';
import {ToolIconSource} from 'llm/chat-library/tool-details/types';
import {LLMShimmerText} from 'llm/loading/LLMShimmer';
import {GRAY_DARKER} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {assertNever} from 'util/Types';

import {agentProgressStyles} from './ChatProgress';
import {ThinkingDuotoneSpinner} from './ThinkingSpinner';

export interface ToolActivityItemProps {
  icon: ToolIconSource;
  displayName: string;
  status?: ToolCallStatus;
  isSkipped?: boolean;
  simplifiedStatusIcons?: boolean;
  /**
   * Once the whole turn is finished, completed checks are de-emphasized to a light gray so a
   * settled turn doesn't stay lit up with green. Only affects the simplified completed icon.
   */
  deemphasizeCompleted?: boolean;
  onClick?: () => void;
  testComponent: string;
  testKey?: string;
}

const CHECK_STROKE_LENGTH = 12;
const CHECK_DRAW_MS = 450;

const pulseInProgressDot = keyframes({
  '0%, 100%': {opacity: 0.35, transform: 'scale(0.85)'},
  '50%': {opacity: 1, transform: 'scale(1)'},
});

const drawCompletedCheck = keyframes({
  '0%': {strokeDashoffset: CHECK_STROKE_LENGTH},
  '100%': {strokeDashoffset: 0},
});

const useSimplifiedStatusIconStyles = createStyles((theme) => ({
  container: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    height: 16,
    justifyContent: 'center',
    width: 16,
  },
  circle: {
    alignItems: 'center',
    borderRadius: '50%',
    boxSizing: 'border-box',
    display: 'flex',
    height: 16,
    justifyContent: 'center',
    width: 16,
  },
  inProgressCircle: {
    border: `2px solid ${Color.BLUE_600}`,
  },
  inProgressDot: {
    animation: `${pulseInProgressDot} 1.5s ease-in-out infinite`,
    backgroundColor: Color.BLUE_600,
    borderRadius: '50%',
    height: 6,
    width: 6,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      opacity: 1,
    },
  },
  pendingCircle: {
    border: `2px solid ${Color.GRAY_400}`,
  },
  completedCircle: {
    backgroundColor: Color.GREEN_400,
  },
  completedCircleDeemphasized: {
    backgroundColor: Color.GRAY_400,
  },
  completedCheck: {
    display: 'block',
    height: 12,
    width: 12,
  },
  completedCheckStroke: {
    animation: `${drawCompletedCheck} ${CHECK_DRAW_MS}ms ease-out forwards`,
    fill: 'none',
    stroke: theme.white,
    strokeDasharray: CHECK_STROKE_LENGTH,
    strokeDashoffset: CHECK_STROKE_LENGTH,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
    strokeWidth: 2,
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
      strokeDashoffset: 0,
    },
  },
  cancelledGlyph: {
    color: Color.GRAY_500,
    fontSize: 16,
  },
}));

function SimplifiedToolStatusIcon({
  status,
  deemphasizeCompleted = false,
}: {
  status: ToolCallStatus;
  deemphasizeCompleted?: boolean;
}): JSX.Element {
  const {classes, cx} = useSimplifiedStatusIconStyles();

  switch (status) {
    case ToolCallStatus.UNKNOWN:
    case ToolCallStatus.QUEUED:
      return (
        <span aria-label="Pending" className={classes.container} role="img">
          <span aria-hidden={true} className={cx(classes.circle, classes.pendingCircle)} />
        </span>
      );
    case ToolCallStatus.RUNNING:
      return (
        <span aria-label="In progress" className={classes.container} role="img">
          <span aria-hidden={true} className={cx(classes.circle, classes.inProgressCircle)}>
            <span className={classes.inProgressDot} />
          </span>
        </span>
      );
    case ToolCallStatus.COMPLETED:
      return (
        <span
          aria-label="Completed"
          className={cx(
            classes.container,
            classes.circle,
            classes.completedCircle,
            deemphasizeCompleted && classes.completedCircleDeemphasized
          )}
          role="img"
        >
          <svg aria-hidden={true} className={classes.completedCheck} viewBox="0 0 16 16">
            <polyline className={classes.completedCheckStroke} points="3.5,8.2 6.7,11 12.3,5" />
          </svg>
        </span>
      );
    case ToolCallStatus.CANCELLED:
      return (
        <BenchlingIcon
          altText="Cancelled"
          className={cx(classes.container, classes.cancelledGlyph)}
          icon="circle-xmark"
        />
      );
    default:
      return assertNever(status);
  }
}

/**
 * Shared tool activity item for displaying a tool call in progress.
 * Shows an icon and the tool's display name.
 */
export function ToolActivityItem({
  icon,
  displayName,
  status = ToolCallStatus.UNKNOWN,
  isSkipped = false,
  simplifiedStatusIcons = false,
  deemphasizeCompleted = false,
  onClick,
  testComponent,
  testKey,
}: ToolActivityItemProps): JSX.Element {
  const resolvedStatus = isSkipped ? ToolCallStatus.CANCELLED : status;
  const contentOpacity = getToolCallVisualOpacity(resolvedStatus);
  const resolvedDisplayName =
    resolvedStatus === ToolCallStatus.QUEUED && !simplifiedStatusIcons
      ? `${displayName} ${QUEUED_TOOL_DISPLAY_LABEL}`
      : isSkipped && !displayName.endsWith(SKIPPED_TOOL_DISPLAY_LABEL)
        ? `${displayName} ${SKIPPED_TOOL_DISPLAY_LABEL}`
        : displayName;

  return (
    <div
      {...getTestProps({component: testComponent, element: 'activityItem', key: testKey})}
      className={css(agentProgressStyles.activityItem, onClick && agentProgressStyles.activityItemClickable)}
      onClick={onClick}
    >
      {simplifiedStatusIcons ? (
        <SimplifiedToolStatusIcon status={resolvedStatus} deemphasizeCompleted={deemphasizeCompleted} />
      ) : resolvedStatus === ToolCallStatus.RUNNING ? (
        <div className={css(agentProgressStyles.activityExecutingIcon)}>
          <ThinkingDuotoneSpinner />
        </div>
      ) : (
        <ToolIconDisplay
          icon={icon}
          glyphClassName={css(agentProgressStyles.activityIcon)}
          imgClassName={css(agentProgressStyles.activityIconImg)}
          status={resolvedStatus}
          iconColor={GRAY_DARKER}
        />
      )}
      <div
        className={css(agentProgressStyles.activityDescription)}
        style={{opacity: contentOpacity}}
        {...getTestProps({
          component: testComponent,
          element: 'activityDescription',
          key: testKey,
        })}
      >
        <span
          className={css(agentProgressStyles.toolDescription)}
          {...getTestProps({
            component: testComponent,
            element: 'toolDescription',
            key: testKey,
          })}
        >
          {resolvedStatus === ToolCallStatus.RUNNING ? (
            <LLMShimmerText
              {...getTestProps({
                component: testComponent,
                element: 'shimmerText',
                key: testKey,
              })}
            >
              {resolvedDisplayName}
            </LLMShimmerText>
          ) : (
            resolvedDisplayName
          )}
        </span>
      </div>
    </div>
  );
}
